import { unparse } from "papaparse";
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Col, Container, Form, InputGroup, Row, Spinner, Tab, Tabs } from "react-bootstrap";
import { FiletypeCsv, PlusLg, Search, X } from "react-bootstrap-icons";
import { SemesterRecord, ThesisRecord, ThesisStatusRecord, ThesisTypeRecord } from "../generated/pocketbase";
import { pb } from "../utils/pocketbase";
import { Button } from "./Button";
import { SelectSpinner } from "./SelectSpinner";
import { ThesisList } from "./ThesisList";
import { UpsertThesis } from "./UpsertThesis";

export const ThesisTypes = () => {
  const [search, setSearch] = useState("");
  const [selectedThesis, setSelectedThesis] = useState<ThesisRecord | "new">();
  const [thesis, setThesis] = useState<ThesisRecord[]>([]);
  const [selectedSemester, setSelectedSemester] = useState<SemesterRecord>();
  const [semester, setSemester] = useState<SemesterRecord[]>([]);
  const [thesisStatus, setThesisStatus] = useState<ThesisStatusRecord[]>([]);
  const [selectedThesisType, setSelectedThesisType] = useState<ThesisTypeRecord>();
  const [thesisTypes, setThesisTypes] = useState<ThesisTypeRecord[]>([]);
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();


  const fetchAllThesis = () =>
    pb.collection("Thesis").getFullList({ sort: "-updated" });

  const refetchThesis = useCallback(() => {
    setErrorMessage(undefined);
    setPending(true);

    fetchAllThesis()
      .then((data) => setThesis(data))
      .catch(error => setErrorMessage(error.message))
      .finally(() => setPending(false));
  }, [])

  useEffect(() => {
    setErrorMessage(undefined);
    setPending(true);

    Promise.all([
      pb.collection("Semester").getFullList({ sort: "+order" }),
      pb.collection("ThesisType").getFullList({ sort: "+name" }),
      pb.collection("ThesisStatus").getFullList(),
      fetchAllThesis(),
    ])
      .then(([semesterResponse, typesResponse, statusResponse, thesisResponse]) => {
        setSemester(semesterResponse);
        setSelectedSemester(semesterResponse[semesterResponse.length - 1]);
        setThesisTypes(typesResponse);
        setSelectedThesisType(typesResponse[0]);
        setThesisStatus(statusResponse);
        setThesis(thesisResponse);
      })
      .catch(error => setErrorMessage(error.message))
      .finally(() => setPending(false));
  }, []);

  const handleExport = () => {
    const data = thesis.map(t => ({
      ...t,
      submissionDate: t.submissionDate?.slice(0, 10),
      presentationDate: t.presentationDate?.slice(0, 10),
      lastContactDate: t.lastContactDate?.slice(0, 10),
      semester: semester.find(s => s.id === t.semester)?.name,
      type: thesisTypes.find(type => type.id === t.type)?.name,
      status: thesisStatus.find(s => s.id === t.status)?.name,
    }));

    const columns: (keyof ThesisRecord)[] = [
      "semester",
      "type",
      "supervisorName",
      "relatedProject",
      "salutation",
      "name",
      "status",
      "topic",
      "submissionDate",
      "presentationDate",
      "lastContactDate",
    ];

    const csv = unparse(data, {
      header: true,
      quotes: true,
      newline: "\n",
      columns,
    });

    const csvBlob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(csvBlob);
    link.download = new Date().toISOString().slice(0, 10) + "-student-overview-thesis.csv";
    link.click();
  }

  return (
    <Container>
      {pending ? (
        <Spinner animation="border" />
      ) : errorMessage ? (
        <Alert variant="danger">{errorMessage}</Alert>
      ) : thesisTypes.length <= 0 || !selectedThesisType || !selectedSemester ? (
        <Alert variant="danger">Something went wrong, please contact his highness</Alert>
      ) : (
        <>
          <Row className="mb-5">
            <Col md="6">
              <InputGroup>
                {search && (
                  <Button variant="outline-secondary" onClick={() => setSearch("")}><X /></Button>
                )}
                <Form.Control
                  placeholder="Type to search"
                  className="border-end-0"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                <InputGroup.Text className="bg-white"><Search /></InputGroup.Text>
              </InputGroup>
            </Col>
            <Col>
              <SelectSpinner
                disabled={!!search}
                options={semester}
                value={selectedSemester}
                getLabel={s => s.name}
                getKey={s => s.id}
                onChange={setSelectedSemester}
              />
            </Col>
            <Col className="d-flex justify-content-end">
              <Button
                icon={FiletypeCsv}
                variant="outline-secondary"
                onClick={handleExport}
                className="me-2"
              >
                Export
              </Button>
              <Button
                icon={PlusLg}
                variant="outline-primary"
                onClick={() => setSelectedThesis("new")}
              >
                Create
              </Button>
            </Col>
          </Row>
          {search ? (
            <ThesisList
              semester={semester}
              types={thesisTypes}
              status={thesisStatus}
              thesis={thesis}
              search={search}
              onClickThesis={setSelectedThesis}
            />
          ) : (
            <Tabs
              fill
              defaultActiveKey={selectedThesisType.id}
              onSelect={(key) => key && setSelectedThesisType(thesisTypes.find(t => t.id === key))}
              id="thesisTypes"
              className="mb-3"
            >
              {thesisTypes.map((type) => {
                const countThesis = thesis.filter(t => t.type === type.id && t.semester === selectedSemester.id).length;
                return (
                  <Tab
                    eventKey={type.id}
                    title={
                      <>
                        {type.name}
                        {!!countThesis && (
                          <small>
                            <Badge bg="primary" className="ms-1" pill>
                              {countThesis}
                            </Badge>
                          </small>
                        )}
                      </>
                    }
                    key={type.id}
                  >
                    <ThesisList
                      semester={semester}
                      types={thesisTypes}
                      status={thesisStatus}
                      thesis={thesis.filter(t => t.type === type.id && t.semester === selectedSemester.id)}
                      onClickThesis={setSelectedThesis}
                    />
                  </Tab>
                );
              })}
            </Tabs>
          )}
          {selectedThesis && (
            <UpsertThesis
              thesis={selectedThesis !== "new" ? selectedThesis : undefined}
              selectedThesisType={selectedThesisType}
              thesisTypes={thesisTypes}
              selectedSemester={selectedSemester}
              semester={semester}
              thesisStatus={thesisStatus}
              onClose={(saved) => {
                setSelectedThesis(undefined);
                if (saved) {
                  refetchThesis();
                }
              }}
            />
          )}
        </>
      )}
    </Container>
  );
};